import React from "react";
import { Autocomplete, TextField, Tooltip, Box } from "@mui/material";
import { useFormikContext } from "formik";

export interface LookupOption {
  id: number | null;
  name: string;
}

export interface GenericLookupFieldProps {
  name: string;
  label: string;
  options: LookupOption[];
  idFieldName: string;
  nameFieldName: string;
  disabled?: boolean;
  placeholder?: string;
  tooltip?: string;
  required?: boolean;
  allowCreate?: boolean;
  size?: "small" | "medium";
}

const GenericLookupField: React.FC<GenericLookupFieldProps> = ({
  name,
  label,
  options,
  idFieldName,
  nameFieldName,
  disabled = false,
  placeholder,
  tooltip,
  required = false,
  allowCreate = true,
  size = "small",
}) => {
  const { values, errors, touched, setFieldValue, setFieldTouched } = useFormikContext<any>();

  // Helper function to get nested values safely
  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  // Helper function to safely convert error to string
  const safeErrorToString = (error: any): string => {
    if (typeof error === 'string') return error;
    if (typeof error === 'object' && error !== null) {
      return Object.values(error).join(', ');
    }
    return '';
  };

  const idValue = getNestedValue(values, idFieldName);
  const nameValue = getNestedValue(values, nameFieldName);
  const fieldError = getNestedValue(errors, idFieldName) || getNestedValue(errors, nameFieldName);
  const fieldTouched = getNestedValue(touched, idFieldName) || getNestedValue(touched, nameFieldName);

  // Find current value
  const currentOption = idValue !== null ? options.find(option => option.id === idValue) : null;
  const displayValue = currentOption || (nameValue ? { id: null, name: nameValue } : null);

  const handleChange = (event: React.SyntheticEvent, newValue: LookupOption | string | null) => {
    if (typeof newValue === 'string') {
      // User typed a new value
      if (allowCreate) {
        setFieldValue(idFieldName, null);
        setFieldValue(nameFieldName, newValue);
      }
    } else if (newValue) {
      // User selected an existing option
      setFieldValue(idFieldName, newValue.id);
      setFieldValue(nameFieldName, null);
    } else {
      // User cleared the field
      setFieldValue(idFieldName, null);
      setFieldValue(nameFieldName, null);
    }
  };

  const handleBlur = () => {
    setFieldTouched(idFieldName, true);
    setFieldTouched(nameFieldName, true);
  };

  const handleInputChange = (_: React.SyntheticEvent, inputValue: string) => {
    if (allowCreate && inputValue && !options.find(option => option.name === inputValue)) {
      setFieldValue(idFieldName, null);
      setFieldValue(nameFieldName, inputValue);
    }
  };

  const lookupField = (
    <Autocomplete
      fullWidth
      size={size}
      options={options}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option.name || '';
      }}
      isOptionEqualToValue={(option, value) => {
        if (typeof option === 'string' || typeof value === 'string') {
          return option === value;
        }
        return option.id === value.id && option.name === value.name;
      }}
      value={displayValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onBlur={handleBlur}
      disabled={disabled}
      freeSolo={allowCreate}
      selectOnFocus
      handleHomeEndKeys
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={fieldTouched && Boolean(fieldError)}
          helperText={fieldTouched && fieldError ? safeErrorToString(fieldError) : ""}
        />
      )}
      sx={{ mb: 2 }}
    />
  );

  return tooltip ? (
    <Tooltip title={tooltip} placement="top">
      <Box>{lookupField}</Box>
    </Tooltip>
  ) : (
    lookupField
  );
};

export default GenericLookupField;
